import { Warning as WarningIcon } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { yellow } from "@mui/material/colors";
import _flatten from "lodash/flatten";
import { useGetAccountingInvoices } from "misc/customHooks";
import { isIntegratedApplicationActive } from "misc/permissionHelpers";
import { renderCurrency, renderFullName } from "misc/renderHelpers";
import { BookingRecordType, InvoiceRecordType } from "pages/Booking/Booking.types";
import { GeneratedDocumentRecordType } from "pages/DocumentTemplate/DocumentTemplate.types";
import { FailedInvoiceResponse } from "pages/Invoice/Invoice.types";
import { AttendeeRecordType } from "pages/SharedRecords.types";
import { Fragment } from "react";
import { useTranslate } from "react-admin";

import { GeneratedDocumentDownloadButton } from "./GenerateDocumentDialogButton";

export type InvoiceStatus = { sentToAccounting?: 200 | string };

function AccountingInvoicePresentIcon(props: { isLoading: boolean; isPresent: boolean }) {
  const { isLoading, isPresent } = props;
  const translate = useTranslate();

  if (isLoading) return <CircularProgress size={15} />;

  if (isPresent) {
    const tooltipTitle = translate("hints.accounting_invoice_already_present");
    return (
      <Tooltip title={tooltipTitle} arrow>
        <WarningIcon sx={{ color: yellow[600] }} />
      </Tooltip>
    );
  } else {
    return null;
  }
}

type InvoiceOverviewTablePropsType = {
  invoices: InvoiceRecordType[];
  showExternalAccountingFeature: boolean;
  failedInvoiceResponsesWithBookings?: {
    booking: BookingRecordType<AttendeeRecordType>;
    invoiceResponse: FailedInvoiceResponse;
  }[];
  generatedDocuments?: GeneratedDocumentRecordType[];
};

export default function InvoiceOverviewTable(props: InvoiceOverviewTablePropsType) {
  const {
    invoices,
    showExternalAccountingFeature,
    failedInvoiceResponsesWithBookings = [],
    generatedDocuments,
  } = props;
  const translate = useTranslate();
  const failuresPresent = failedInvoiceResponsesWithBookings.length > 0;
  const generatedDocumentsPresent = generatedDocuments !== undefined;
  const { accountingInvoices, isLoading: isLoadingAccountingInvoices } = useGetAccountingInvoices(
    invoices.map((i) => i.id),
    // TODO[accounting]: The scopevisio check is temporary and will be replaced by webhooks
    showExternalAccountingFeature && isIntegratedApplicationActive("scopevisio"),
  );

  if (invoices.length === 0 && !failuresPresent) return null;

  const renderError = (res: FailedInvoiceResponse) => {
    const details = res.message || res.contact_info;
    return details ? _flatten(details).join(", ") : translate("invoice.error");
  };

  return (
    <Fragment>
      <Typography gutterBottom>
        {failuresPresent ? translate("invoice.created_with_failures_text") : translate("invoice.created_text")}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate("resources.bookings.fields.attendee")}</TableCell>
              <TableCell>{translate("resources.prices.fields.amount")}</TableCell>
              {showExternalAccountingFeature && (
                <TableCell data-testid="sent-to-accounting-table-head-cell" align="center" />
              )}
              {generatedDocumentsPresent && <TableCell />}
              {failuresPresent && <TableCell>{translate("error")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => {
              const accountingInvoicePresent = !!accountingInvoices.find((ai) => ai.number === invoice.invoice_number);
              const generatedDocument = generatedDocuments?.find((d) => d.related_id === invoice.id);

              return (
                <TableRow key={invoice.id} hover>
                  <TableCell>{renderFullName(invoice)}</TableCell>
                  <TableCell>{invoice.price && renderCurrency()(invoice.price)}</TableCell>
                  {showExternalAccountingFeature && (
                    <TableCell data-testid="sent-to-accounting-table-body-cell">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <AccountingInvoicePresentIcon
                          isPresent={accountingInvoicePresent}
                          isLoading={isLoadingAccountingInvoices}
                        />
                      </Box>
                    </TableCell>
                  )}
                  {generatedDocumentsPresent && (
                    <TableCell align="right">
                      {generatedDocument && (
                        <GeneratedDocumentDownloadButton ids={[generatedDocument.id]} mode="icon" />
                      )}
                    </TableCell>
                  )}
                  {failuresPresent && <TableCell />}
                </TableRow>
              );
            })}
            {failedInvoiceResponsesWithBookings.map(({ invoiceResponse, booking }) => (
              <TableRow key={`failed-inovice-${booking.id}`} hover>
                <TableCell>{renderFullName(booking.attendee)}</TableCell>
                <TableCell />
                {showExternalAccountingFeature && <TableCell data-testid="sent-to-accounting-failed-table-body-cell" />}
                {generatedDocumentsPresent && <TableCell />}
                <TableCell>
                  <Typography variant="body2" color="error">
                    {renderError(invoiceResponse)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Fragment>
  );
}
